import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { mediaSrc } from "@/lib/api";
import { speak, speakSequence, cancelSpeech, playStepAudio } from "@/lib/speak";
import { ZOOD_MASCOT } from "@/lib/brand";
import TraceDemo from "@/components/engines/TraceDemo";
import { Volume2, ArrowRight, ArrowLeft, Lightbulb, ListChecks, BookOpen, CheckCircle2, Sparkles } from "lucide-react";

function narrationFor(step) {
  if (!step) return "";
  if (step.type === "teach") return step.body || step.title;
  if (step.type === "example") return step.body || step.title;
  if (step.type === "demo") return step.body || step.title;
  if (step.type === "concept") return `${step.term}. ${step.definition}`;
  if (step.type === "check") return step.check?.question || step.title;
  return "";
}

export default function TutorialPlayer({ tutorial, color = "#0D9488", onDone, subjectName, autoplay = false }) {
  const steps = tutorial?.steps || [];
  const [i, setI] = useState(0);
  const [revealed, setRevealed] = useState(0); // for worked-example steps
  const [picked, setPicked] = useState(null); // for check
  const step = steps[i];
  const advanced = useRef(false);
  const tokenRef = useRef(0);
  const advanceRef = useRef(() => {});

  const say = useCallback((t, onEnd) => t && speak(t, "nova", { speed: 0.9, onEnd }), []);
  // Step-aware playback: honors per-step recording OR AI voice choice.
  const sayStep = useCallback((s, t, onEnd) => t && playStepAudio(s, t, { speed: 0.9, onEnd }), []);

  const isLast = i === steps.length - 1;
  const exampleSteps = step && step.type === "example" ? (step.steps || []) : [];
  const canAdvance = !step ? false
    : step.type === "check" ? picked === step.check.answer
    : step.type === "example" ? revealed >= exampleSteps.length
    : true;

  advanceRef.current = () => { if (isLast) { onDone(); } else { setI(i + 1); } };

  useEffect(() => {
    setRevealed(0); setPicked(null); advanced.current = false;
    tokenRef.current += 1;
    const myToken = tokenRef.current;
    const s = steps[i];
    if (!s) return undefined;
    const finishStep = () => { if (tokenRef.current === myToken && !advanced.current) { advanced.current = true; setTimeout(() => advanceRef.current(), s.type === "demo" ? 4500 : 900); } };
    const start = setTimeout(() => {
      if (!autoplay) { sayStep(s, narrationFor(s)); return; }
      if (s.type === "example") {
        // Worked-example sequences fall back to browser TTS chaining (audio-note
        // recordings don't currently split by sub-step). Only fire the recorded
        // audio for the main step body; then speak sub-steps via cached TTS.
        if (s.audio_note_id) {
          sayStep(s, s.body || s.title, () => {
            const items = (s.steps || []).map((sub, k) => ({ text: sub, before: () => setRevealed(k + 1) }));
            speakSequence(items, { speed: 0.9, gap: 500, onDone: finishStep });
          });
        } else {
          const subs = s.steps || [];
          const items = [{ text: s.body || s.title }];
          subs.forEach((sub, k) => items.push({ text: sub, before: () => setRevealed(k + 1) }));
          speakSequence(items, { speed: 0.9, gap: 500, onDone: finishStep });
        }
      } else if (s.type === "check") {
        sayStep(s, s.check?.question || s.title);
      } else {
        sayStep(s, narrationFor(s), finishStep);
      }
    }, 350);
    return () => { clearTimeout(start); cancelSpeech(); };
  }, [i, autoplay]); // eslint-disable-line

  // Autoplay: advance shortly after a correct quick-check answer.
  useEffect(() => {
    if (autoplay && step?.type === "check" && picked === step.check.answer) {
      const t = setTimeout(() => advanceRef.current(), 1600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [picked]); // eslint-disable-line

  if (!step) return null;

  const next = () => advanceRef.current();

  return (
    <div className="mx-auto max-w-2xl px-4 py-6" data-testid="tutorial-player">
      {/* progress */}
      <div className="mb-5 flex items-center gap-2">
        {steps.map((s, k) => (
          <div key={k} className="h-2 flex-1 overflow-hidden rounded-full bg-[#F2ECE1]">
            <div className="h-full rounded-full transition-all" style={{ width: k <= i ? "100%" : "0%", background: color }} />
          </div>
        ))}
      </div>
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#17BEBB]/15 px-3 py-1 text-xs font-bold text-[#0B7F74]">
          <Sparkles size={13} /> Learn · Step {i + 1} of {steps.length}
        </span>
        <button onClick={() => sayStep(step, narrationFor(step))} data-testid="tutorial-replay" className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold text-[#0D9488] hover:bg-[#17BEBB]/10">
          <Volume2 size={14} /> Read again
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10, transition: { duration: 0.15 } }}
          className="rounded-[28px] border border-[#E5DECF] bg-white p-6 shadow-sm">
          {/* mascot + narration */}
          <div className="flex items-start gap-3">
            <motion.img src={ZOOD_MASCOT} alt="Zoodle" className="h-16 w-16 shrink-0 drop-shadow"
              animate={{ y: [0, -6, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} />
            <div className="relative mt-1 flex-1 rounded-2xl rounded-tl-none border border-[#E5DECF] bg-[#FAF7F2] p-4">
              {step.type === "concept" ? (
                <>
                  <div className="flex items-center gap-1.5 text-xs font-bold" style={{ color }}><Lightbulb size={14} /> New word</div>
                  <div className="mt-1 font-heading text-2xl font-extrabold text-[#1F2937]">{step.term}</div>
                  <p className="mt-1 font-body text-[#374151]">{step.definition}</p>
                </>
              ) : (
                <>
                  {step.title && <div className="font-heading text-xl font-extrabold text-[#1F2937]">{step.title}</div>}
                  {step.body && <p className="mt-1.5 font-body text-[#374151]">{step.body}</p>}
                </>
              )}
            </div>
          </div>

          {step.media_url && (
            <img src={mediaSrc(step.media_url)} alt="" data-testid="tutorial-image" className="mx-auto mt-4 max-h-56 rounded-2xl border border-[#E5DECF] object-contain" />
          )}

          {/* teach bullets */}
          {step.type === "teach" && (step.bullets || []).length > 0 && (
            <ul className="mt-4 space-y-2">
              {step.bullets.map((b, k) => (
                <motion.li key={k} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + k * 0.12 }}
                  className="flex items-start gap-2 font-body text-[#374151]"><BookOpen size={16} className="mt-0.5 shrink-0" style={{ color }} /> {b}</motion.li>
              ))}
            </ul>
          )}

          {/* handwriting demo */}
          {step.type === "demo" && <TraceDemo trace={step.trace} color={color} />}

          {/* worked example */}
          {step.type === "example" && (
            <div className="mt-4 space-y-2" data-testid="example-steps">
              {exampleSteps.slice(0, revealed).map((s, k) => (
                <motion.div key={k} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-2 rounded-xl bg-[#17BEBB]/8 px-3 py-2 font-body text-[#1F2937]">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: color }}>{k + 1}</span>
                  <span>{s}</span>
                </motion.div>
              ))}
              {revealed < exampleSteps.length && (
                <button onClick={() => { const n = revealed + 1; setRevealed(n); say(exampleSteps[revealed]); }} data-testid="reveal-step"
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-white" style={{ background: color }}>
                  <ListChecks size={15} /> {revealed === 0 ? "Show me how" : "Next step"}
                </button>
              )}
            </div>
          )}

          {/* quick check */}
          {step.type === "check" && (
            <div className="mt-4 space-y-2" data-testid="tutorial-check">
              <div className="font-heading text-lg font-bold text-[#1F2937]">{step.check.question}</div>
              {step.check.options.map((o, k) => {
                const chosen = picked === k;
                const correct = k === step.check.answer;
                const show = picked !== null;
                let cls = "border-[#E5DECF] bg-white hover:bg-[#F2ECE1]";
                if (show && correct) cls = "border-[#10B981] bg-[#10B981]/10";
                else if (show && chosen && !correct) cls = "border-[#E05A47] bg-[#E05A47]/10";
                return (
                  <button key={k} disabled={picked === step.check.answer} onClick={() => { setPicked(k); say(correct ? "That's right!" : "Try again!"); }}
                    data-testid={`check-opt-${k}`} className={`flex w-full items-center justify-between rounded-2xl border-2 px-4 py-3 text-left font-body font-semibold transition-colors ${cls}`}>
                    <span>{o}</span>
                    {show && correct && <CheckCircle2 size={18} className="text-[#10B981]" />}
                  </button>
                );
              })}
              {picked !== null && picked !== step.check.answer && <p className="text-sm font-bold text-[#B45309]">Not quite — try again!</p>}
              {picked === step.check.answer && <p className="text-sm font-bold text-[#047857]">Great job! 🎉</p>}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* nav */}
      <div className="mt-5 flex items-center justify-between">
        <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} data-testid="tutorial-back"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] bg-white px-4 py-2.5 font-bold text-[#1F2937] hover:bg-[#F2ECE1] disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
        <button onClick={next} disabled={!canAdvance} data-testid="tutorial-next"
          className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-heading font-bold text-white shadow-md disabled:opacity-40" style={{ background: color }}>
          {isLast ? "Start practice" : "Next"} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
